import {
  actualPrice,
  formattedPrimaryPrice,
  formattedSecondaryPrice,
  clientFormattedPriceAfterDiscount,
  clientFormattedPriceBeforeDiscount,
  hasAnyDiscount,
  hasDiscount,
} from '@wix/wixstores-client-core/dist/es/src/productOptions/productUtils';
import {getFormattingFunction} from '@wix/wixstores-client-core';
import {IOptionSelectionVariant} from '@wix/wixstores-client-core/dist/es/src/types/product';
import type {ControllerFlowAPI} from '@wix/yoshi-flow-editor';

import {IProduct} from '@wix/wixstores-graphql-schema/dist/es/src';
import {ISubscriptionPlan} from '../components/ProductOptions/SubscriptionPlans/SubscriptionPlan/SubscriptionPlan';
import {CurrentPrice, UserInput} from '../types/app-types';
import {SPECS} from '../specs';

// eslint-disable-next-line @typescript-eslint/no-extraneous-class
export class ProductPriceService {
  private readonly product: IProduct;
  private readonly selectedVariant: IOptionSelectionVariant;
  private readonly selectedPlan: ISubscriptionPlan;
  private readonly shouldRenderPriceRange: boolean;
  private readonly shouldShowDiscountedVariants: boolean;
  private readonly formatPrice?: (price: number | string) => string;
  private readonly flowAPI?: ControllerFlowAPI;
  private readonly isClientFormatting: boolean;

  constructor({
    product,
    selectedVariant,
    selectedPlan,
    shouldRenderPriceRange,
    shouldShowDiscountedVariants,
    flowAPI,
    experiments,
  }) {
    this.product = product;
    this.selectedVariant = selectedVariant;
    this.selectedPlan = selectedPlan;
    this.shouldRenderPriceRange = shouldRenderPriceRange;
    this.shouldShowDiscountedVariants = shouldShowDiscountedVariants;
    this.isClientFormatting = experiments.enabled(SPECS.OnlineStoresCurrencyClientFormatting);
    if (this.isClientFormatting) {
      this.flowAPI = flowAPI;
      this.formatPrice = getFormattingFunction({
        flowAPI,
        formatingCurrency: product.currency,
      });
    }
  }

  private get ref() {
    return this.selectedVariant || this.product;
  }

  private get hasPricePerUnit() {
    return !!this.product.pricePerUnitData;
  }

  private get hasSelectedPlan() {
    return !!this.selectedPlan;
  }

  private get pricesPerUnit() {
    if (!this.hasPricePerUnit) {
      return null;
    }

    let productDiscountPricePerUnit = null;
    let variantDiscountPricePerUnit = null;

    if (this.shouldShowDiscountedVariants) {
      const productDiscountPPU = this.product?.itemDiscount?.automaticDiscountPricePerUnit;
      const productDiscountPPUFormatted = this.clientOrServerFormatted(
        productDiscountPPU,
        this.product?.itemDiscount?.formattedAutomaticDiscountPricePerUnit
      );
      productDiscountPricePerUnit = productDiscountPPU
        ? {formattedPricePerUnit: productDiscountPPUFormatted, pricePerUnit: productDiscountPPU}
        : null;

      const discountedVariantPPU = this.selectedVariant?.automaticDiscount?.automaticDiscountPricePerUnit;
      const discountedVariantPPUFormatted = this.clientOrServerFormatted(
        discountedVariantPPU,
        this.selectedVariant?.automaticDiscount?.formattedAutomaticDiscountPricePerUnit
      );
      variantDiscountPricePerUnit = discountedVariantPPU
        ? {formattedPricePerUnit: discountedVariantPPUFormatted, pricePerUnit: discountedVariantPPU}
        : null;
    }

    const ref: Partial<{
      formattedPricePerUnit: IProduct['formattedPricePerUnit'];
      pricePerUnit: IProduct['pricePerUnit'];
    }> =
      this.selectedVariant?.subscriptionPlans?.[this.selectedPlan?.id] ||
      variantDiscountPricePerUnit ||
      this.selectedVariant ||
      this.product.subscriptionPlans?.list.find((plan) => plan.id === this.selectedPlan?.id) ||
      productDiscountPricePerUnit ||
      this.product;

    return {
      baseMeasurementUnit: this.product.pricePerUnitData.baseMeasurementUnit,
      baseQuantity: this.product.pricePerUnitData.baseQuantity,
      formattedPricePerUnit: this.clientOrServerFormatted(ref.pricePerUnit, ref.formattedPricePerUnit),
      pricePerUnit: ref.pricePerUnit,
    };
  }

  private get hasDiscount() {
    return (
      hasAnyDiscount(this.product, this.selectedVariant) ||
      hasDiscount(this.product) ||
      this.product.itemDiscount?.priceAfterDiscount
    );
  }

  private clientOrServerFormatted(numeric: number | string, serverFormatted: string): string {
    return this.formatPrice ? this.formatPrice(numeric) : serverFormatted;
  }

  private get productPricing(): CurrentPrice {
    const currentActualPrice = actualPrice(this.product, this.selectedVariant);
    const discountPrices = this.hasDiscount ? this.discountedPrices() : null;
    const formattedActualPrice = discountPrices
      ? discountPrices.priceAfterDiscount
      : this.clientOrServerFormatted(this.ref.price, this.ref.formattedPrice);
    return {
      isZeroPrice: currentActualPrice === 0,
      formattedActualPrice,
      formattedPassivePrice: discountPrices?.formattedPassivePrice ?? null,
    };
  }

  private discountedPrices(): {priceAfterDiscount: string; formattedPassivePrice: string} {
    const productVariant = {...this.product, ...this.ref};
    const isFreeProduct = productVariant.price === 0 && productVariant.comparePrice === 0;
    return this.isClientFormatting
      ? this.clientFormattedDiscountPrices(productVariant, isFreeProduct)
      : this.serverFormattedDiscountPrices(productVariant, isFreeProduct);
  }

  private clientFormattedDiscountPrices(
    productVariant: IProduct,
    isFreeProduct: boolean
  ): {priceAfterDiscount: string; formattedPassivePrice: string} {
    return {
      priceAfterDiscount: isFreeProduct
        ? null
        : clientFormattedPriceAfterDiscount({
            product: productVariant,
            flowAPI: this.flowAPI,
            variant: this.selectedVariant,
            includeVariantDiscount: this.shouldShowDiscountedVariants,
          }),
      formattedPassivePrice: clientFormattedPriceBeforeDiscount({
        product: productVariant,
        flowAPI: this.flowAPI,
        variant: this.selectedVariant,
      }),
    };
  }

  private serverFormattedDiscountPrices(
    productVariant: IProduct,
    isFreeProduct: boolean
  ): {priceAfterDiscount: string; formattedPassivePrice: string} {
    return {
      priceAfterDiscount: isFreeProduct
        ? null
        : formattedPrimaryPrice(productVariant, this.selectedVariant, this.shouldShowDiscountedVariants),
      formattedPassivePrice: formattedSecondaryPrice(productVariant, this.selectedVariant),
    };
  }

  private get subscriptionPricing(): CurrentPrice {
    const ref: Partial<{
      formattedPrice: IProduct['formattedPrice'];
      formattedComparePrice: IProduct['formattedComparePrice'];
      price: IProduct['price'];
      comparePrice: IProduct['comparePrice'];
      frequency: CurrentPrice['frequency'];
    }> =
      this.selectedVariant?.subscriptionPlans?.[this.selectedPlan.id] ||
      this.selectedVariant ||
      this.product.subscriptionPlans.list.find((plan) => plan.id === this.selectedPlan.id);

    return {
      formattedActualPrice: this.clientOrServerFormatted(
        ref.comparePrice ?? ref.price,
        ref.formattedComparePrice || ref.formattedPrice
      ),
      frequency: this.selectedPlan.frequency,
      interval: this.selectedPlan.interval,
    };
  }

  private getPriceRange(): Partial<Pick<CurrentPrice, 'formattedFromPrice'>> {
    if (this.product.priceRange) {
      const hasDiscountedFromPrice = this.shouldShowDiscountedVariants && this.product.itemDiscount?.priceAfterDiscount;
      let formattedFromPrice: string;
      if (hasDiscountedFromPrice) {
        formattedFromPrice = this.clientOrServerFormatted(
          this.product.itemDiscount.priceAfterDiscountAmount,
          this.product.itemDiscount.priceAfterDiscount
        );
      } else {
        formattedFromPrice = this.clientOrServerFormatted(
          this.product.priceRange.fromPrice,
          this.product.priceRange.fromPriceFormatted
        );
      }
      return {formattedFromPrice};
    }
    return {};
  }

  private shouldRenderPriceRangeFinal({selection, subscriptionPlan}: UserInput): boolean {
    const isAnyOptionSelected = selection.some((s) => !!s);
    return this.shouldRenderPriceRange && !isAnyOptionSelected && subscriptionPlan.length === 0;
  }

  public getCurrentPricing(userInputs: UserInput): CurrentPrice {
    const hasRecurringPlan = this.hasSelectedPlan && !this.selectedPlan.isOneTimePurchase;
    const pricing = hasRecurringPlan ? this.subscriptionPricing : this.productPricing;
    const priceRange = this.shouldRenderPriceRangeFinal(userInputs) ? this.getPriceRange() : {};

    return {
      ...this.pricesPerUnit,
      ...pricing,
      ...priceRange,
    };
  }
}
